import { combineReducers, createStore, ReducersMapObject } from 'redux';
import { getInitialReduxStoreState } from '../restore';
import { brokerReducer } from './features/broker.slice';
import { btoConfigReducer, resetBtoConfig } from './features/btoConfig.slice';
import { consentsReducer } from './features/consents.slice';
import { creditApplicationReducer, resetCreditApplication } from './features/creditApplication.slice';
import { customReducer, resetCustom } from './features/custom.slice';
import { customerReducer, resetCustomer } from './features/customer.slice';
import { dashboardReducer } from './features/dashboard.slice';
import { dataStorageNewReducer, resetDataStorageNew } from './features/dataStorage.redux';
import { loadingReducer } from './features/loading.slice';
import { notesReducer, resetNotes } from './features/notes.slice';
import { notificationCenterReducer } from './features/notificationCenter.slice';
import { productFamilyReducer, resetProductType } from './features/product-family.slice';
import { quoteReducer, resetQuote } from './features/quote.slice';

const reducersMap = {
  productType: productFamilyReducer,
  custom: customReducer,
  btoConfig: btoConfigReducer,
  broker: brokerReducer,
  quote: quoteReducer,
  customer: customerReducer,
  creditApplication: creditApplicationReducer,
  dashboard: dashboardReducer,
  dataStorageNew: dataStorageNewReducer,
  consents: consentsReducer,
  notes: notesReducer,
  loading: loadingReducer,
  notificationCenter: notificationCenterReducer,
} satisfies ReducersMapObject<any, any>;

const rootReducer = combineReducers(reducersMap);

export type RootState = ReturnType<typeof rootReducer>;

type StoreModuleName = keyof typeof reducersMap;

const store = createStore(
  rootReducer,
  getInitialReduxStoreState(),
  (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION__({
      trace: true,
    })
);
/* eslint-enable */

const storeResetHandlers: Record<StoreModuleName, () => void> = {
  productType() {
    store.dispatch(resetProductType());
  },
  custom() {
    store.dispatch(resetCustom());
  },
  btoConfig() {
    store.dispatch(resetBtoConfig());
  },
  broker() {
    /* noop */
  },
  quote() {
    store.dispatch(resetQuote());
  },
  customer() {
    store.dispatch(resetCustomer());
  },
  creditApplication() {
    store.dispatch(resetCreditApplication());
  },
  dashboard() {
    /* noop */
  },
  dataStorageNew() {
    store.dispatch(resetDataStorageNew());
  },
  consents() {
    /* noop */
  },
  notes() {
    store.dispatch(resetNotes());
  },
  loading() {
    /* noop */
  },
  notificationCenter() {
    /* noop */
  },
};

export { store };

/**
 * - Reset redux data to work on a new quote.
 */
export const resetQuoteSpecificReduxData = () => {
  Object.values(storeResetHandlers).forEach((resetHandler) => resetHandler());
};

/**
 * - Wrapper for selectors for clearly traceable access to store.
 * - Static means it does not observe store value and is not reactive.
 */
export const staticSelector = <Selected = unknown>(selector: (state: RootState) => Selected) => {
  return selector(store.getState());
};

export const getCacheableState = () => {
  const currentState = { ...store.getState() };
  const modulesToOmit: StoreModuleName[] = ['loading'];

  modulesToOmit.forEach((moduleName) => {
    delete currentState[moduleName];
  });

  return currentState;
};
